/**
 * Geo-IP Language Detection
 * Automatically detects user's country and suggests appropriate language
 */

// Country to language mapping
const countryToLanguage = {
  // Spanish-speaking countries
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', VE: 'es', CL: 'es',
  EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
  SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
  
  // French-speaking countries
  FR: 'fr', BE: 'fr', CH: 'fr', CA: 'fr', LU: 'fr', MC: 'fr',
  SN: 'fr', CI: 'fr', ML: 'fr', BF: 'fr', NE: 'fr', TD: 'fr',
  
  // Chinese-speaking regions
  CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh', SG: 'zh',
  
  // Vietnamese
  VN: 'vi',
  
  // Korean
  KR: 'ko', KP: 'ko',
  
  // Tagalog/Filipino
  PH: 'tl',
  
  // Arabic-speaking countries
  SA: 'ar', AE: 'ar', EG: 'ar', IQ: 'ar', JO: 'ar', KW: 'ar',
  LB: 'ar', LY: 'ar', MA: 'ar', OM: 'ar', QA: 'ar', SY: 'ar',
  TN: 'ar', YE: 'ar', BH: 'ar', DZ: 'ar', SD: 'ar',
  
  // English-speaking countries (default)
  US: 'en', GB: 'en', AU: 'en', NZ: 'en', IE: 'en', ZA: 'en',
  IN: 'en', PK: 'en', NG: 'en', KE: 'en', GH: 'en'
};

// Supported languages in the app
const supportedLanguages = ['en', 'es', 'fr', 'zh', 'vi', 'ko', 'tl', 'ar'];

/**
 * Fetch user's country from IP geolocation API
 * Uses free ipapi.co service
 */
export async function detectCountryFromIP() {
  try {
    const response = await fetch('https://ipapi.co/json/', {
      timeout: 5000
    });
    
    if (!response.ok) {
      throw new Error('Geo API failed');
    }
    
    const data = await response.json();
    return data.country_code || null;
  } catch (error) {
    console.warn('Geo-IP detection failed:', error.message);
    return null;
  }
}

/**
 * Get suggested language based on country code
 */
export function getLanguageFromCountry(countryCode) {
  if (!countryCode) return null;
  
  const language = countryToLanguage[countryCode.toUpperCase()];
  
  // Only return if it's a supported language
  if (language && supportedLanguages.includes(language)) {
    return language;
  }
  
  return null;
}

/**
 * Check if user has already set a language preference
 */
export function hasLanguagePreference() {
  return localStorage.getItem('i18nextLng') !== null || 
         localStorage.getItem('geoLanguagePrompted') === 'true';
}

/**
 * Mark that user has been prompted for geo language
 */
export function markGeoLanguagePrompted() {
  localStorage.setItem('geoLanguagePrompted', 'true');
}

/**
 * Get language name for display
 */
export function getLanguageName(code) {
  const names = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
    zh: '中文',
    vi: 'Tiếng Việt',
    ko: '한국어',
    tl: 'Tagalog',
    ar: 'العربية'
  };
  return names[code] || code;
}

/**
 * Get flag emoji for language
 */
export function getLanguageFlag(code) {
  const flags = {
    en: '🇺🇸',
    es: '🇪🇸',
    fr: '🇫🇷',
    zh: '🇨🇳',
    vi: '🇻🇳',
    ko: '🇰🇷',
    tl: '🇵🇭',
    ar: '🇸🇦'
  };
  return flags[code] || '🌐';
}

export default {
  detectCountryFromIP,
  getLanguageFromCountry,
  hasLanguagePreference,
  markGeoLanguagePrompted,
  getLanguageName,
  getLanguageFlag,
  supportedLanguages
};
